// Purchase orders and the Time & Action Plan: the workflow rules of FALCON PLM v2.0.41, shared by the
// server (which enforces them) and the screens (which only use them to decide what to offer).
import { cartonMetrics, normalizePacking, type PackingProfile } from "./items.js";

// ---- Workflow of one PO line ------------------------------------------------------------------------
export type RoundStatus = "Waiting" | "Approved" | "Rejected";
export type BulkStatus = "Waiting" | "In Progress" | "Complete";
export type QcStatus = "Pending" | "Pass" | "Fail";
export interface Round { status: RoundStatus; date?: string; note?: string; noteBy?: string; noteAt?: string }
export interface Workflow {
  sample1?: Round; sample2?: Round; fit1?: Round; fit2?: Round; pps1?: Round; pps2?: Round;
  bulk?: { status: BulkStatus; date?: string };
  qc?: { status: QcStatus; date?: string; by?: string; note?: string };
  qc2?: { status: QcStatus; date?: string; by?: string; note?: string };
  readyToShip?: { value: boolean; date?: string };
  received?: { value: boolean; date?: string; by?: string };
  /** Stage 2.6E: the gold seal (approved PPS kept as the bulk reference) recorded for this line. */
  goldSeal?: { number: string; date: string };
  /** Stage 2.6F: Straight To Production — a repeat order that skips the waived sample stages. */
  stp?: StpRecord;
  /** Bulk fabric test: Bulk Production started before every lot passed (a warning), and an Admin's waiver. */
  fabricTestWarning?: { at: string; by: string; text: string };
  fabricTestOverride?: { at: string; by: string; reason: string };
  /** Bulk Production set Complete by an Admin without a QC result (e.g. an order shipped before the organisation used
   *  Falcon). QC shows "Skipped (Admin)"; it is never a Pass, so QC pass rates and supplier performance leave it out. */
  qcSkipped?: QcSkipRecord;
  /** The line's bulk fabric test status, set in Sampling (by hand, or from the latest lot saved). Tracking only: it never
   *  blocks production, QC, ready to ship or shipment. `noReport` = passed without a test report or lot details. */
  fabricStatus?: FabricStatusRecord;
  /** Stages rejected / failed at least the organisation's alert number of times (Admins were alerted). */
  escalations?: Partial<Record<"sample" | "fit" | "pps" | "fabric" | "qc", { count: number; at: string }>>;
}
export interface QcSkipRecord { reason: string; note?: string; by: string; at: string }
/** The reasons an Admin can give for completing Bulk Production without QC. */
export const QC_SKIP_REASONS = ["Shipped before using Falcon", "QC done outside Falcon", "Other"] as const;
export type FabricStatus = "none" | "pending" | "pass" | "fail";
export interface FabricStatusRecord { status: FabricStatus; at: string; by: string; source: "manual" | "lot"; comment?: string; noReport?: { at: string; by: string } }
export const FABRIC_STATUS_TEXT: Record<FabricStatus, string> = { none: "Not started", pending: "Pending", pass: "Pass", fail: "Fail" };
export type SealKind = "sample" | "fit" | "pps";
/** An extra sample an STP line still needs: new dye-lot shade approval, or the shipment (black seal) sample. */
export interface StpExtraRound { status: RoundStatus; date?: string; by?: string; note?: string }
export interface StpRecord {
  poNumber: string; sealNumber: string; waived: SealKind[];
  /** Approved although some checks failed (the reasons are kept). */
  exception: boolean; reasons: string[]; note: string; by: string; at: string;
  shade?: StpExtraRound; shipment?: StpExtraRound;
}
export const ROUND_STAGES = ["sample1", "sample2", "fit1", "fit2", "pps1", "pps2"] as const;
export type RoundStage = (typeof ROUND_STAGES)[number];
export type Stage = RoundStage | "bulk" | "qc" | "qc2" | "readyToShip" | "received";
export const STAGE_LABEL: Record<Stage, string> = {
  sample1: "Sample", sample2: "2nd Sample", fit1: "Fit Sample", fit2: "2nd Fit Sample", pps1: "PPS", pps2: "2nd PPS",
  bulk: "Bulk Production", qc: "QC", qc2: "2nd QC", readyToShip: "Ready to Ship", received: "Received",
};
/** Which Time & Action Plan column permission and which action each stage needs (v2.0.41 permissions). */
export const STAGE_PERMISSION: Record<Stage, { column: string; action: string }> = {
  sample1: { column: "sample", action: "approveSamples" }, sample2: { column: "sample", action: "approveSamples" },
  fit1: { column: "fitSample", action: "approveSamples" }, fit2: { column: "fitSample", action: "approveSamples" },
  pps1: { column: "pps", action: "approvePPS" }, pps2: { column: "pps", action: "approvePPS" },
  bulk: { column: "bulkProduction", action: "updateBulk" },
  qc: { column: "qc", action: "performQC" }, qc2: { column: "qc2", action: "performQC" },
  readyToShip: { column: "readyToShip", action: "editWorkflow" },
  received: { column: "received", action: "markReceived" },
};

export function normalizeWorkflow(w: any): Workflow {
  const out: Workflow = {};
  const round = (x: any): Round | undefined => (x && ["Waiting", "Approved", "Rejected"].includes(x.status) ? { ...x } : undefined);
  for (const k of ROUND_STAGES) { const r = round(w?.[k]); if (r) out[k] = r; }
  if (w?.bulk && ["Waiting", "In Progress", "Complete"].includes(w.bulk.status)) out.bulk = { ...w.bulk };
  for (const k of ["qc", "qc2"] as const) if (w?.[k] && ["Pending", "Pass", "Fail"].includes(w[k].status)) out[k] = { ...w[k] };
  if (w?.readyToShip) out.readyToShip = { value: !!w.readyToShip.value, date: w.readyToShip.date };
  if (w?.received) out.received = { value: !!w.received.value, date: w.received.date, by: w.received.by };
  if (w?.goldSeal?.number) out.goldSeal = { number: String(w.goldSeal.number), date: String(w.goldSeal.date || "") };
  if (w?.fabricTestWarning?.at) out.fabricTestWarning = { at: String(w.fabricTestWarning.at), by: String(w.fabricTestWarning.by || ""), text: String(w.fabricTestWarning.text || "") };
  if (w?.fabricTestOverride?.reason) out.fabricTestOverride = { at: String(w.fabricTestOverride.at || ""), by: String(w.fabricTestOverride.by || ""), reason: String(w.fabricTestOverride.reason) };
  if (w?.qcSkipped?.reason) out.qcSkipped = { reason: String(w.qcSkipped.reason), ...(w.qcSkipped.note ? { note: String(w.qcSkipped.note) } : {}), by: String(w.qcSkipped.by || ""), at: String(w.qcSkipped.at || "") };
  if (w?.fabricStatus && ["none", "pending", "pass", "fail"].includes(w.fabricStatus.status)) {
    const f = w.fabricStatus;
    out.fabricStatus = { status: f.status, at: String(f.at || ""), by: String(f.by || ""), source: f.source === "lot" ? "lot" : "manual",
      ...(f.comment ? { comment: String(f.comment) } : {}), ...(f.noReport?.at ? { noReport: { at: String(f.noReport.at), by: String(f.noReport.by || "") } } : {}) };
  }
  if (w?.escalations && typeof w.escalations === "object") {
    const e: Workflow["escalations"] = {};
    for (const k of ["sample", "fit", "pps", "fabric", "qc"] as const) if (w.escalations[k]?.count) e[k] = { count: Number(w.escalations[k].count), at: String(w.escalations[k].at || "") };
    if (Object.keys(e).length) out.escalations = e;
  }
  // An STP record (a repeat relying on an earlier seal, or an exception with no earlier order).
  if (w?.stp?.sealNumber || w?.stp?.poNumber || w?.stp?.at) {
    const x = w.stp, extra = (e: any): StpExtraRound | undefined => (e && ["Waiting", "Approved", "Rejected"].includes(e.status) ? { ...e } : undefined);
    out.stp = {
      poNumber: String(x.poNumber || ""), sealNumber: String(x.sealNumber || ""), waived: (["sample", "fit", "pps"] as SealKind[]).filter((k) => Array.isArray(x.waived) && x.waived.includes(k)),
      exception: !!x.exception, reasons: Array.isArray(x.reasons) ? x.reasons.map(String) : [], note: String(x.note || ""), by: String(x.by || ""), at: String(x.at || ""),
      ...(extra(x.shade) ? { shade: extra(x.shade) } : {}), ...(extra(x.shipment) ? { shipment: extra(x.shipment) } : {}),
    };
  }
  return out;
}
/** A sample stage skipped because the line is Straight To Production. */
export const waived = (w: Workflow, kind: SealKind) => !!w.stp?.waived.includes(kind);
/** Bulk started with neither a gold seal nor STP: the Time & Action Plan warns (it is allowed). */
export function goldSealMissing(w: Workflow, itemType: string): boolean {
  return needsSampling(itemType) && !w.goldSeal && !w.stp;
}

const st = (w: Workflow, k: RoundStage): RoundStatus => w[k]?.status || "Waiting";
/** Sample, fit sample or PPS approved (first round, or second round after a rejection). */
export function roundApproved(w: Workflow, kind: "sample" | "fit" | "pps"): boolean {
  const a = st(w, `${kind}1` as RoundStage), b = st(w, `${kind}2` as RoundStage);
  return a === "Approved" || (a === "Rejected" && b === "Approved");
}
/** Only garments go through sampling; fabric and trim lines start at Bulk Production (as v2.0.41). */
export const needsSampling = (itemType: string) => itemType === "product";
export const bulkStatus = (w: Workflow): BulkStatus => w.bulk?.status || "Waiting";
export function qcState(w: Workflow): "" | "Pending" | "Pass" | "Fail" | "Skipped" {
  if (bulkStatus(w) === "Waiting") return "";
  const a = w.qc?.status || "Pending";
  const result = a === "Pass" ? "Pass" : a === "Fail" ? (w.qc2?.status === "Pass" ? "Pass" : w.qc2?.status === "Fail" ? "Fail" : "Pending") : "Pending";
  // A real Pass always wins; otherwise an Admin's skip lets the line carry on without a QC result.
  return result !== "Pass" && w.qcSkipped ? "Skipped" : result;
}
/** QC is out of the way: passed, or skipped by an Admin. */
export const qcPassed = (w: Workflow) => qcState(w) === "Pass" || qcState(w) === "Skipped";
export const qcSkipped = (w: Workflow) => qcState(w) === "Skipped";
export const isReceived = (w: Workflow) => !!w.received?.value;
/** Approved, or waived by STP. */
export const roundDone = (w: Workflow, kind: SealKind) => roundApproved(w, kind) || waived(w, kind);
const samplingDone = (w: Workflow, itemType: string) => !needsSampling(itemType) || (roundDone(w, "sample") && roundDone(w, "fit") && roundDone(w, "pps"));

/** The stage waiting for action (highlighted blue in the Time & Action Plan). */
export function currentStage(w: Workflow, itemType: string): Stage | "" {
  if (isReceived(w)) return "";
  if (needsSampling(itemType)) {
    for (const kind of ["sample", "fit", "pps"] as const) {
      if (roundDone(w, kind)) continue;
      return st(w, `${kind}1` as RoundStage) === "Rejected" ? (`${kind}2` as Stage) : (`${kind}1` as Stage);
    }
  }
  if (bulkStatus(w) === "Waiting") return "bulk";
  if (!qcPassed(w)) return w.qc?.status === "Fail" ? "qc2" : "qc";
  if (bulkStatus(w) !== "Complete") return "bulk";
  if (!w.readyToShip?.value) return "readyToShip";
  return "received";
}

/** Dashboard pipeline stage of a line. */
export type PipelineStage = "Sample" | "Fit Sample" | "PPS" | "Bulk" | "QC" | "Ready to Ship" | "Awaiting Receipt" | "Complete" | "Cancelled";
export const PIPELINE: PipelineStage[] = ["Sample", "Fit Sample", "PPS", "Bulk", "QC", "Ready to Ship", "Awaiting Receipt", "Complete"];
export function pipelineStage(w: Workflow, itemType: string, poStatus: string): PipelineStage {
  if (poStatus === "cancelled") return "Cancelled";
  if (isReceived(w)) return "Complete";
  if (needsSampling(itemType)) {
    if (!roundDone(w, "sample")) return "Sample";
    if (!roundDone(w, "fit")) return "Fit Sample";
    if (!roundDone(w, "pps")) return "PPS";
  }
  if (bulkStatus(w) === "Waiting") return "Bulk";
  if (!qcPassed(w)) return "QC";
  if (bulkStatus(w) !== "Complete") return "Bulk";
  if (!w.readyToShip?.value) return "Ready to Ship";
  return "Awaiting Receipt";
}

/**
 * Check a change to one stage against the v2.0.41 workflow rules. Returns the reason it's refused, or null.
 * `override` = an Admin correcting a mistake (only the order of stages is still checked).
 * `qcSkip` = an Admin completing Bulk Production without QC (only with `override`, only to Complete).
 */
export function checkStageChange(w: Workflow, itemType: string, stage: Stage, value: string | boolean, override = false, qcSkip = false): string | null {
  if (qcSkip) {
    if (!override) return "Only an Admin can complete Bulk Production without QC.";
    if (stage !== "bulk" || value !== "Complete") return "QC can only be skipped when Bulk Production is set to Complete.";
    if (qcState(w) === "Pass") return "QC has already passed: choose Complete.";
    if (qcSkipped(w) && bulkStatus(w) === "Complete") return "Bulk Production is already Complete with QC skipped.";
  }
  const bulk = bulkStatus(w);
  if ((ROUND_STAGES as readonly string[]).includes(stage)) {
    if (!needsSampling(itemType)) return "Fabric and trim lines don't go through sampling.";
    if (!["Waiting", "Approved", "Rejected"].includes(String(value))) return "Choose Waiting, Approved or Rejected.";
    if (bulk !== "Waiting" && !override) return "Sampling can't change once Bulk Production has started.";
    const kind = stage.slice(0, -1) as "sample" | "fit" | "pps";
    if (waived(w, kind) && !override) return "This stage is waived: the line is Straight To Production (STP).";
    const second = stage.endsWith("2");
    if (second && st(w, `${kind}1` as RoundStage) !== "Rejected") return `The 2nd round opens only when the first ${STAGE_LABEL[`${kind}1` as Stage]} is rejected.`;
    if (kind === "fit" && !roundDone(w, "sample")) return "Fit Sample opens once the Sample is approved.";
    if (kind === "pps" && !roundDone(w, "fit")) return "PPS opens once the Fit Sample is approved.";
    return null;
  }
  if (stage === "bulk") {
    if (!["Waiting", "In Progress", "Complete"].includes(String(value))) return "Choose Waiting, In Progress or Complete.";
    if (!samplingDone(w, itemType)) return "Bulk Production opens once the PPS is approved.";
    if (bulk === "Waiting" && value !== "Waiting" && w.stp?.shade && w.stp.shade.status !== "Approved") return "Straight To Production: the new dye-lot shade approval is needed before Bulk Production starts.";
    if (override) return value === "Complete" && !qcPassed(w) && !qcSkip ? "Bulk Production can't be Complete until QC has passed." : null;
    if (w.readyToShip?.value) return "Bulk Production can't change after the line is Ready to Ship.";
    if (bulk === "Complete") return "Bulk Production is complete. An Admin can correct it if needed.";
    if (value === "Complete" && !qcPassed(w)) return "Bulk Production can't be Complete until QC has passed.";
    if (value === "Waiting" && bulk !== "Waiting") return "Bulk Production can't go back to Waiting. An Admin can correct it if needed.";
    return null;
  }
  if (stage === "qc" || stage === "qc2") {
    if (!["Pending", "Pass", "Fail"].includes(String(value))) return "Choose Pending, Pass or Fail.";
    if (bulk === "Waiting") return "QC opens once Bulk Production is In Progress.";
    if (stage === "qc2" && w.qc?.status !== "Fail") return "2nd QC opens only when the first QC fails.";
    if (!override && stage === "qc" && w.qc?.status && w.qc.status !== "Pending") return "The QC result is recorded. An Admin can correct it if needed.";
    if (!override && stage === "qc2" && w.qc2?.status && w.qc2.status !== "Pending") return "The 2nd QC result is recorded. An Admin can correct it if needed.";
    return null;
  }
  if (stage === "readyToShip") {
    if (bulk !== "Complete") return "Ready to Ship opens once Bulk Production is Complete.";
    if (value && w.stp?.shipment && w.stp.shipment.status !== "Approved") return "Straight To Production: the shipment sample must be approved before the line is Ready to Ship.";
    if (!value && w.received?.value) return "The line is already received.";
    return null;
  }
  if (stage === "received") {
    if (value && !w.readyToShip?.value) return "Mark the line Ready to Ship first.";
    if (!value && !override) return "Only an Admin can undo Received.";
    return null;
  }
  return "Unknown stage.";
}

/** v2.0.41 shows a date under a stage only for final results. */
export function stageDateShows(stage: Stage, value: string | boolean): boolean {
  if ((ROUND_STAGES as readonly string[]).includes(stage)) return value === "Approved" || value === "Rejected";
  if (stage === "bulk") return value === "Complete";
  if (stage === "qc" || stage === "qc2") return value === "Pass" || value === "Fail";
  return value === true;
}

/** Apply an allowed change and return the new workflow. */
export function applyStageChange(w: Workflow, stage: Stage, value: string | boolean, today: string, by: string): Workflow {
  const n: Workflow = structuredClone(w);
  const date = stageDateShows(stage, value) ? today : undefined;
  if ((ROUND_STAGES as readonly string[]).includes(stage)) n[stage as RoundStage] = { ...(n[stage as RoundStage] || {}), status: value as RoundStatus, date };
  else if (stage === "bulk") {
    n.bulk = { status: value as BulkStatus, date };
    if (value !== "Complete" && n.readyToShip?.value) n.readyToShip = { value: false };
    // Taking Bulk Production off Complete undoes an Admin's QC skip (the line waits for QC again).
    if (value !== "Complete") delete n.qcSkipped;
  } else if (stage === "qc" || stage === "qc2") {
    n[stage] = { ...(n[stage] || {}), status: value as QcStatus, date, by };
    if (stage === "qc" && value !== "Fail") delete n.qc2;
  } else if (stage === "readyToShip") n.readyToShip = { value: !!value, date };
  else if (stage === "received") n.received = { value: !!value, date, by: value ? by : undefined };
  return n;
}

export function stageValue(w: Workflow, stage: Stage): string | boolean {
  if ((ROUND_STAGES as readonly string[]).includes(stage)) return st(w, stage as RoundStage);
  if (stage === "bulk") return bulkStatus(w);
  if (stage === "qc" || stage === "qc2") return w[stage]?.status || "Pending";
  if (stage === "readyToShip") return !!w.readyToShip?.value;
  return !!w.received?.value;
}

// ---- Order status and lateness ----------------------------------------------------------------------------
export type OrderStatus = "Open" | "Complete" | "Cancelled";
export function orderStatus(poStatus: string, lines: Array<{ workflow: Workflow }>): OrderStatus {
  if (poStatus === "cancelled") return "Cancelled";
  return lines.length && lines.every((l) => isReceived(l.workflow)) ? "Complete" : "Open";
}
export const lineStatus = (poStatus: string, w: Workflow): OrderStatus => (poStatus === "cancelled" ? "Cancelled" : isReceived(w) ? "Complete" : "Open");
/** Late: open, and the original ETD has passed (v2.0.41). At risk: the working ETD is later than the original. */
export function lateness(status: OrderStatus, etd: string | null, workingEtd: string | null, today: string): "" | "late" | "risk" {
  if (status !== "Open") return "";
  if (etd && etd < today) return "late";
  if (etd && workingEtd && workingEtd > etd) return "risk";
  return "";
}

// ---- Lines, totals and document groups --------------------------------------------------------------------
export interface PoLineInput {
  id?: string; itemId: string; itemType: "product" | "fabric" | "trim"; code?: string; description?: string; colour?: string; pantone?: string;
  sizeSet?: string[]; qtyBySize?: Record<string, number>; quantity?: number; unit?: string; unitPrice?: number;
}
export const lineQuantity = (l: { itemType: string; qtyBySize?: Record<string, number>; quantity?: number }) =>
  l.itemType === "product" ? Object.values(l.qtyBySize || {}).reduce((a, b) => a + (Number(b) || 0), 0) : Number(l.quantity || 0);
const r2 = (n: number) => Math.round(n * 100) / 100;
export const lineAmount = (qty: number, price: number) => r2(qty * price);

/** Garment lines with the same size run share a table on the PO document (v2.0.41); fabrics and trims have their own. */
export function documentGroups<T extends { itemType: string; sizeSet: string[] }>(lines: T[]): Array<{ kind: "sizes" | "fabric" | "trim"; sizes: string[]; lines: T[] }> {
  const groups: Array<{ kind: "sizes" | "fabric" | "trim"; sizes: string[]; lines: T[] }> = [];
  for (const l of lines) {
    const kind = l.itemType === "product" ? "sizes" : l.itemType === "fabric" ? "fabric" : "trim";
    const key = kind === "sizes" ? JSON.stringify(l.sizeSet) : kind;
    let g = groups.find((x) => (x.kind === "sizes" ? JSON.stringify(x.sizes) : x.kind) === key);
    if (!g) groups.push((g = { kind, sizes: kind === "sizes" ? l.sizeSet : [], lines: [] }));
    g.lines.push(l);
  }
  return groups;
}

// ---- Container planning (internal, never printed) ------------------------------------------------------------
export interface ContainerType { uid: string; name: string; internalLengthM: number; internalWidthM: number; internalHeightM: number; planningCbm: number; maxPayloadKg: number }
export const DEFAULT_CONTAINERS: ContainerType[] = [
  { uid: "20FT", name: "20FT", internalLengthM: 5.9, internalWidthM: 2.35, internalHeightM: 2.39, planningCbm: 29.9, maxPayloadKg: 28000 },
  { uid: "40FT", name: "40FT", internalLengthM: 12.03, internalWidthM: 2.35, internalHeightM: 2.39, planningCbm: 60.8, maxPayloadKg: 26700 },
  { uid: "40HQ", name: "40HQ", internalLengthM: 12.03, internalWidthM: 2.35, internalHeightM: 2.69, planningCbm: 68.4, maxPayloadKg: 26500 },
];
const pos = (v: unknown) => Math.max(0, Number(v) || 0);
export function normalizeContainer(c: any, i = 0): ContainerType {
  const uid = String(c?.uid || c?.name || `CONT${i + 1}`).trim().slice(0, 20) || `CONT${i + 1}`;
  return { uid, name: String(c?.name || uid).trim().slice(0, 40), internalLengthM: pos(c?.internalLengthM), internalWidthM: pos(c?.internalWidthM), internalHeightM: pos(c?.internalHeightM), planningCbm: pos(c?.planningCbm), maxPayloadKg: pos(c?.maxPayloadKg) };
}
export const internalCbm = (c: ContainerType) => Math.round(c.internalLengthM * c.internalWidthM * c.internalHeightM * 100) / 100;

export interface PlanLine { key: string; code: string; qty: number; packing: PackingProfile | null }
export function containerPlan(lines: PlanLine[], containers: ContainerType[], selectedUid: string) {
  const est = lines.map((l) => {
    const p = normalizePacking(l.packing || {});
    const m = cartonMetrics(l.qty, p);
    const cartons = m.cartons;
    return {
      ...l, packing: p, cartons,
      cbm: m.cbm,
      netWeight: p.pieceWeightKg ? l.qty * p.pieceWeightKg : cartons && p.cartonNetWeightKg ? cartons * p.cartonNetWeightKg : 0,
      grossWeight: cartons && p.cartonGrossWeightKg ? cartons * p.cartonGrossWeightKg : 0,
      volumeReady: p.pcsPerCarton > 0 && p.cartonCbm > 0,
      grossReady: p.pcsPerCarton > 0 && p.cartonGrossWeightKg > 0,
    };
  });
  const sum = (f: (x: (typeof est)[number]) => number) => est.reduce((a, x) => a + f(x), 0);
  const totalCbm = sum((x) => x.cbm), totalGross = sum((x) => x.grossWeight);
  const volumeReady = est.length > 0 && est.every((x) => x.volumeReady), grossReady = est.length > 0 && est.every((x) => x.grossReady);
  const sorted = [...containers].sort((a, b) => a.planningCbm - b.planningCbm);
  const suggested = volumeReady ? sorted.find((c) => totalCbm <= c.planningCbm + 0.0001 && (!grossReady || totalGross <= c.maxPayloadKg + 0.01)) || null : null;
  const container = containers.find((c) => c.uid === selectedUid) || null;
  return {
    lines: est, totalPieces: sum((x) => x.qty), totalCartons: sum((x) => x.cartons), totalCbm, totalNetWeight: sum((x) => x.netWeight), totalGrossWeight: totalGross,
    volumeReady, grossReady, missing: est.filter((x) => !x.volumeReady).map((x) => x.code), suggested, container,
    volumeUtil: container && container.planningCbm && volumeReady ? (totalCbm / container.planningCbm) * 100 : null,
    weightUtil: container && container.maxPayloadKg && grossReady ? (totalGross / container.maxPayloadKg) * 100 : null,
  };
}
/** How many whole cartons of one line could be added, or must be removed, to fit the chosen container (v2.0.41). */
export function containerAdjustment(plan: ReturnType<typeof containerPlan>, key: string): { kind: string; text: string } {
  if (!plan.container) return { kind: "none", text: "Choose a container to see the remaining or excess capacity." };
  if (!plan.volumeReady) return { kind: "warn", text: "Enter pieces per carton and carton dimensions for every line to calculate container capacity." };
  const line = plan.lines.find((x) => x.key === key);
  if (!key) return { kind: "none", text: "Choose a line to estimate how many pieces could be added or reduced." };
  if (!line || !line.volumeReady) return { kind: "warn", text: "That line needs pieces per carton and carton dimensions." };
  const c = plan.container, pack = line.packing;
  const weightKnown = plan.grossReady && pack.cartonGrossWeightKg > 0;
  const excessCbm = Math.max(0, plan.totalCbm - c.planningCbm), excessKg = weightKnown ? Math.max(0, plan.totalGrossWeight - c.maxPayloadKg) : 0;
  if (excessCbm > 0 || excessKg > 0) {
    const cartons = Math.max(excessCbm > 0 ? Math.ceil(excessCbm / pack.cartonCbm) : 0, excessKg > 0 ? Math.ceil(excessKg / pack.cartonGrossWeightKg) : 0, 1);
    return { kind: "reduce", text: `Reduce about ${(cartons * pack.pcsPerCarton).toLocaleString("en-GB")} pcs (${cartons} cartons) of ${line.code} to fit the ${c.name}${weightKnown ? " by volume and weight" : " by volume"}.` };
  }
  const byVolume = Math.floor(Math.max(0, c.planningCbm - plan.totalCbm) / pack.cartonCbm);
  const byWeight = weightKnown ? Math.floor(Math.max(0, c.maxPayloadKg - plan.totalGrossWeight) / pack.cartonGrossWeightKg) : Infinity;
  const cartons = Math.max(0, Math.min(byVolume, byWeight));
  if (!cartons) return { kind: "full", text: `No whole extra carton of ${line.code} fits in the ${c.name}.` };
  return { kind: "add", text: `You could add about ${(cartons * pack.pcsPerCarton).toLocaleString("en-GB")} pcs (${cartons} cartons) of ${line.code}${weightKnown ? " within the volume and weight limits" : " by volume (gross weight incomplete)"}.` };
}

// ---- Revisions -------------------------------------------------------------------------------------------------
/** The parts of a PO that make a new revision when they change (as v2.0.41). */
export interface PoBusiness {
  poNumber: string; supplier: string; agent: string; notes: string; container: string;
  lines: Array<{ key: string; code: string; colour: string; sizes: Record<string, number>; quantity: number; unitPrice: number; techpack?: string }>;
}
export function revisionChanges(a: PoBusiness, b: PoBusiness): string[] {
  const out: string[] = [];
  if (a.poNumber !== b.poNumber) out.push(`PO number ${a.poNumber} → ${b.poNumber}`);
  if (a.supplier !== b.supplier) out.push(`Supplier ${a.supplier || "—"} → ${b.supplier || "—"}`);
  if (a.agent !== b.agent) out.push(`Agent ${a.agent || "—"} → ${b.agent || "—"}`);
  if (a.notes !== b.notes) out.push("Supplier notes changed");
  if (a.container !== b.container) out.push(`Container plan ${a.container || "—"} → ${b.container || "—"}`);
  const before = new Map(a.lines.map((l) => [l.key, l])), after = new Map(b.lines.map((l) => [l.key, l]));
  for (const [k, l] of after) {
    const o = before.get(k);
    const name = `${l.code}${l.colour ? ` ${l.colour}` : ""}`;
    if (!o) { out.push(`Added ${name}`); continue; }
    if (o.code !== l.code) out.push(`Line ${o.code} changed to ${l.code}`);
    if (o.colour !== l.colour) out.push(`${l.code} colour ${o.colour || "—"} → ${l.colour || "—"}`);
    if (JSON.stringify(o.sizes) !== JSON.stringify(l.sizes) || o.quantity !== l.quantity) out.push(`${name} quantity ${o.quantity} → ${l.quantity}`);
    if (o.unitPrice !== l.unitPrice) out.push(`${name} price ${o.unitPrice} → ${l.unitPrice}`);
    if ((o.techpack || "") !== (l.techpack || "")) out.push(`${name} Tech Pack revision changed`);
  }
  for (const [k, o] of before) if (!after.has(k)) out.push(`Removed ${o.code}${o.colour ? ` ${o.colour}` : ""}`);
  return out;
}

// ---- Tasks ------------------------------------------------------------------------------------------------------
export const TASK_PRIORITIES = [["low", "Low"], ["normal", "Normal"], ["high", "High"], ["urgent", "Urgent"]] as const;
export const TASK_STATUSES = [["todo", "To Do"], ["in_progress", "In Progress"], ["blocked", "Blocked"], ["complete", "Complete"]] as const;
